import { createSlice } from "@reduxjs/toolkit";
import { createErrorReportingAsyncThunk } from "../../helpers";
import axios, { getData } from "../../../axios";
import {
  RentalBillingClients,
  RentalBillingPreview,
  decodeRentalBillingClients,
  decodeRentalBillingPreview,
} from "./types";

const prefix = "data/rental-billing";

type State = {
  preview: RentalBillingPreview | null;
  // Client ids that have a rental in the selected period. Null until loaded,
  // so the picker can tell "not loaded yet" from "no clients with rentals".
  clientIds: string[] | null;
  loading: boolean;
  invoicing: boolean;
};

const initialState: State = {
  preview: null,
  clientIds: null,
  loading: false,
  invoicing: false,
};

export const loadRentalBillingPreview = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (input: {
    clientId: string;
    periodStart: string;
    periodEnd: string;
  }): Promise<RentalBillingPreview> => {
    return await axios
      .get("/api/consolidated-invoicing/rental-billing", { params: input })
      .then(getData)
      .then(decodeRentalBillingPreview);
  }
);

export const loadRentalBillingClients = createErrorReportingAsyncThunk(
  `${prefix}/load-clients`,
  async (input: {
    periodStart: string;
    periodEnd: string;
  }): Promise<RentalBillingClients> => {
    return await axios
      .get("/api/consolidated-invoicing/rental-billing/clients", {
        params: input,
      })
      .then(getData)
      .then(decodeRentalBillingClients);
  }
);

export const invoiceSelectedRentals = createErrorReportingAsyncThunk(
  `${prefix}/invoice`,
  async (input: {
    clientId: string;
    periodStart: string;
    periodEnd: string;
    rentalIds: string[];
    invoiceRef: string | null;
    dueDate: string | null;
  }): Promise<void> => {
    await axios.post("/api/consolidated-invoicing/rental-invoice", input);
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    clearRentalBillingPreview: (state) => {
      state.preview = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRentalBillingPreview.pending, (state) => {
        // Drop the previous customer/month's rows so nothing stale can be
        // selected or invoiced while the new preview is in flight.
        state.loading = true;
        state.preview = null;
      })
      .addCase(loadRentalBillingPreview.fulfilled, (state, action) => {
        state.loading = false;
        state.preview = action.payload;
      })
      .addCase(loadRentalBillingPreview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadRentalBillingClients.fulfilled, (state, action) => {
        state.clientIds = action.payload;
      })
      .addCase(invoiceSelectedRentals.pending, (state) => {
        state.invoicing = true;
      })
      .addCase(invoiceSelectedRentals.fulfilled, (state) => {
        state.invoicing = false;
      })
      .addCase(invoiceSelectedRentals.rejected, (state) => {
        state.invoicing = false;
      });
  },
});

export const { clearRentalBillingPreview } = slice.actions;
export default slice.reducer;
